import { useEnv } from '@ecomm/data-env-variables/useEnv'
import {
  AppWidget,
  ComparisonTable,
  type ComparisonTableProps,
  ExpertSection,
  ExpertsReviews,
  GuaranteeSection,
  VideoButtonWrapper,
  comparisonTableData,
  transformToComparisonTableData
} from '@ecomm/scout'
import { AffirmBanner } from '@ecomm/shared-components/lib/AffirmBanner'
import { ConfidenceBar } from '@ecomm/shared-components/lib/ConfidenceBar'
import { PageWrapper } from '@ecomm/shared-components/lib/PageWrapper'
import { TrustpilotBanner } from '@ecomm/shared-components/lib/TrustpilotBanner'
import { trustPilotUKHomePageCarousel } from '@ecomm/shared-components/lib/TrustpilotBanner/configurations'
import { TrustpilotUKTopBanner } from '@ecomm/shared-components/lib/TrustpilotTopBanner'
import { UserReviews } from '@ecomm/shared-components/lib/UserReviews'
import { UKHomePageAlarmMoments } from '@ecomm/shared-components/v2/lib/AlarmMoments/UKHomePageAlarmMoments/UKHomePageAlarmMoments'
import { USHomePageAlarmMoments } from '@ecomm/shared-components/v2/lib/AlarmMoments/USHomePageAlarmMoments/USHomePageAlarmMoments'
import {
  safeMapExperiences,
  useCameraOnlyLPCTAExperience
} from '@ecomm/shared-ninetailed'
import { Experience } from '@ecomm/shared-ninetailed-experience'

import { VideoBanner } from '@ecomm/monitoring-components'
import type { TypeOf } from '@simplisafe/ewok'
import {
  getAppWidgetData,
  getExpertReviews,
  useVideoBannerData
} from '../../data/transformData'
import { useStaticQueries } from '../../data/util'
import { QuizBanner } from '../QuizBanner'
import { userReviewsData } from './constants'
import { landerBelowTheFoldSchema } from './schema'

const landerBelowTheFoldPropsSchema = landerBelowTheFoldSchema.omit({
  guaranteeAsset: true,
  guaranteeCopy: true,
  quoteWizardAsset: true,
  videoButtonAsset: true,
  contentfulAffirmBanner: true,
  cameraBannerAsset: true,
  expertSectionAsset: true,
  expertReviews: true
})

type LanderBelowTheFoldPropsSchema = TypeOf<
  typeof landerBelowTheFoldPropsSchema
>

type LanderBelowTheFoldContent = {
  readonly data: LanderBelowTheFoldPropsSchema
}

type ExperienceContentProps = {
  readonly id: string
  readonly json: { readonly tag: string }
  readonly children: React.ReactNode
  readonly variantKey: string
}

export function ExperienceContentWrapper({
  json,
  children,
  variantKey
}: ExperienceContentProps) {
  return json.tag === variantKey ? children : null
}

export function BelowTheFold({ data }: LanderBelowTheFoldContent) {
  const { locale } = useEnv()
  const isUSSite = locale === 'en-US'

  const {
    expertReviewsData,
    expertSectionAssetData,
    affirmBannerData,
    videoButtonAssetData,
    appWidget,
    guaranteeAsset,
    guaranteeCopy,
    quoteWizardAsset
  } = useStaticQueries()

  const { isCameraOnlyVariant } = useCameraOnlyLPCTAExperience()

  const expertReviews = getExpertReviews(
    data.expertReviewsAssets.items,
    locale,
    expertReviewsData.content.reviews
  )

  const appWidgetData = {
    tabs: getAppWidgetData(
      data.appFeaturesCarouselAssets.items,
      locale,
      appWidget.tabs
    ),
    title: appWidget.title,
    showAppRating: true
  }

  const transformedComparisonTableData: ComparisonTableProps =
    transformToComparisonTableData(comparisonTableData[locale])

  const videoAndAffirmBanner = (
    <div className="grid h-36 gap-4 md:h-80 lg:h-full lg:grid-cols-2 lg:gap-8">
      <VideoButtonWrapper videoButtonAsset={videoButtonAssetData} />
      <AffirmBanner data={affirmBannerData} />
    </div>
  )

  const videoBannerData = useVideoBannerData()

  return (
    <PageWrapper>
      <div
        className="max-w-8xl mx-auto mb-16 flex flex-col gap-6 px-4 md:gap-8 md:px-8 lg:gap-12"
        data-testid="BelowTheFold"
      >
        {isUSSite ? (
          <>
            {data.hideVideoAndAffirmBannerExperiment ? (
              <Experience
                {...data.hideVideoAndAffirmBannerExperiment}
                component={ExperienceContentWrapper}
                experiences={safeMapExperiences<ExperienceContentProps>(
                  data.hideVideoAndAffirmBannerExperiment
                )}
                passthroughProps={{
                  children: videoAndAffirmBanner,
                  variantKey: 'control'
                }}
              />
            ) : (
              videoAndAffirmBanner
            )}

            <ConfidenceBar listItems={data.confidenceBar} />
            {isCameraOnlyVariant ? null : (
              <QuizBanner
                backgroundColor="bg-neutral-medium-160"
                image={quoteWizardAsset}
                quoteWizard={data.quoteWizard.quoteWizard}
              />
            )}
            <VideoBanner data={videoBannerData} />
            <USHomePageAlarmMoments />
          </>
        ) : (
          <>
            <TrustpilotUKTopBanner />
            <ConfidenceBar listItems={data.confidenceBar} />
            <QuizBanner
              image={quoteWizardAsset}
              quoteWizard={data.quoteWizard.quoteWizard}
            />
            <TrustpilotBanner
              className="pt-4 md:pt-8"
              widget={trustPilotUKHomePageCarousel}
            />
          </>
        )}
        <ComparisonTable data={transformedComparisonTableData} />
        {!isUSSite && <UKHomePageAlarmMoments />}
        <AppWidget data={{ ...appWidgetData }} />
        <UserReviews {...userReviewsData} />
        <ExpertsReviews reviews={expertReviews} />
        {data.expertReviewsAdditional && data.expertReviewsAdditionalAssets ? (
          <ExpertsReviews
            reviews={getExpertReviews(
              data.expertReviewsAdditionalAssets.items,
              locale,
              data.expertReviewsAdditional.content.reviews
            )}
          />
        ) : null}
        <GuaranteeSection
          copy={new Map<string, string>(guaranteeCopy)}
          image={guaranteeAsset.guaranteeAsset}
          quoteWizard={data.quoteWizard.quoteWizard}
        />
        <ExpertSection data={{ image: expertSectionAssetData }} />
      </div>
    </PageWrapper>
  )
}
